export const CROP_BACKGROUND_OPTIONS = [
  { id: 'white', labelKey: 'Blanc', color: '#ffffff' },
  { id: 'black', labelKey: 'Noir', color: '#000000' },
  { id: 'lightGray', labelKey: 'Gris clair', color: '#e0e0e0' },
  { id: 'darkGray', labelKey: 'Gris foncé', color: '#4a4a4a' },
  { id: 'transparent', labelKey: 'Transparent', color: null },
];

const HEX_COLOR_RE = /^#([0-9a-fA-F]{6})$/;

export function isCropBackgroundHex(value) {
  return typeof value === 'string' && HEX_COLOR_RE.test(value);
}

/** Normalize any CSS hex (#rgb, #rrggbb, optional alpha) to lowercase #rrggbb. */
export function normalizeCropBackgroundHex(value) {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim().toLowerCase();
  const withAlpha = trimmed.match(/^#([0-9a-f]{6})([0-9a-f]{2})?$/);
  if (withAlpha) {
    return `#${withAlpha[1]}`;
  }
  const short = trimmed.match(/^#([0-9a-f]{3})$/);
  if (short) {
    const [r, g, b] = short[1];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return null;
}

export function resolveCropBackgroundColor(backgroundId) {
  const custom = normalizeCropBackgroundHex(backgroundId);
  if (custom) {
    return custom;
  }
  return CROP_BACKGROUND_OPTIONS.find((option) => option.id === backgroundId)?.color ?? '#ffffff';
}

export function isTransparentCropBackground(backgroundId) {
  return backgroundId === 'transparent';
}

/** Solid color shown in the `<input type="color">` control (never transparent). */
export function resolveCropBackgroundColorInputValue(backgroundId) {
  if (isTransparentCropBackground(backgroundId)) {
    return '#ffffff';
  }
  return resolveCropBackgroundColor(backgroundId);
}

/** Persistable background id: preset id, `transparent`, or `#rrggbb`. */
export function serializeCropBackground(backgroundId) {
  if (isTransparentCropBackground(backgroundId)) {
    return 'transparent';
  }
  const hex = normalizeCropBackgroundHex(backgroundId);
  if (hex) {
    return hex;
  }
  if (CROP_BACKGROUND_OPTIONS.some((option) => option.id === backgroundId)) {
    return backgroundId;
  }
  return 'white';
}

export function parseStoredCropBackground(value) {
  if (!value || typeof value !== 'string') {
    return 'white';
  }
  if (value === 'transparent') {
    return 'transparent';
  }
  if (CROP_BACKGROUND_OPTIONS.some((option) => option.id === value)) {
    return value;
  }
  return normalizeCropBackgroundHex(value) || 'white';
}

/**
 * Store backgroundColor beside crop pixels in the JSON column (no schema migration).
 * Strip unknown fields before feeding react-easy-crop / getCroppedImg.
 */
export function packCroppedAreaPixels(pixels, backgroundId) {
  const backgroundColor = serializeCropBackground(backgroundId);
  if (!pixels || !Number.isFinite(Number(pixels.width)) || !Number.isFinite(Number(pixels.height))) {
    return { backgroundColor };
  }
  return {
    x: Number(pixels.x) || 0,
    y: Number(pixels.y) || 0,
    width: Number(pixels.width),
    height: Number(pixels.height),
    backgroundColor,
  };
}

export function unpackCroppedAreaPixels(raw) {
  if (raw == null) {
    return { pixels: null, backgroundColor: 'white' };
  }
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object') {
      return { pixels: null, backgroundColor: 'white' };
    }
    const backgroundColor = parseStoredCropBackground(parsed.backgroundColor);
    const width = Number(parsed.width);
    const height = Number(parsed.height);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      return { pixels: null, backgroundColor };
    }
    return {
      pixels: {
        x: Number(parsed.x) || 0,
        y: Number(parsed.y) || 0,
        width,
        height,
      },
      backgroundColor,
    };
  } catch {
    return { pixels: null, backgroundColor: 'white' };
  }
}

export function supportsEyeDropper() {
  return typeof window !== 'undefined' && typeof window.EyeDropper === 'function';
}

/**
 * Screen eyedropper (Chrome / Edge). Returns sRGB hex (#rrggbb).
 * @returns {Promise<{ ok: true, color: string } | { ok: false, reason: 'unsupported'|'aborted'|'error' }>}
 */
export async function pickColorWithEyeDropper() {
  if (!supportsEyeDropper()) {
    return { ok: false, reason: 'unsupported' };
  }
  try {
    const dropper = new window.EyeDropper();
    const result = await dropper.open();
    const color = normalizeCropBackgroundHex(result?.sRGBHex);
    if (!color) {
      return { ok: false, reason: 'error' };
    }
    return { ok: true, color };
  } catch (error) {
    if (error?.name === 'AbortError') {
      return { ok: false, reason: 'aborted' };
    }
    return { ok: false, reason: 'error' };
  }
}

export function rgbToHex(r, g, b) {
  const toHex = (n) =>
    Math.max(0, Math.min(255, Math.round(Number(n) || 0)))
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Sample a pixel color from an HTMLImageElement (or canvas-backed img) at client coords.
 * Used when EyeDropper is unavailable — pick from the photo on the crop canvas.
 * @returns {string|null} #rrggbb or null
 */
export function sampleHexColorFromImageElement(img, clientX, clientY) {
  if (!img?.naturalWidth || !img.naturalHeight) {
    return null;
  }
  const rect = img.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return null;
  }
  if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) {
    return null;
  }

  const x = Math.floor(((clientX - rect.left) / rect.width) * img.naturalWidth);
  const y = Math.floor(((clientY - rect.top) / rect.height) * img.naturalHeight);
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    return null;
  }
  try {
    ctx.drawImage(img, x, y, 1, 1, 0, 0, 1, 1);
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
    return rgbToHex(r, g, b);
  } catch {
    // Tainted canvas (cross-origin) — ignore
    return null;
  }
}

/** Round crop must stay 1:1 — otherwise react-easy-crop draws a circle in a non-square box (ellipse). */
export function resolveCropperAspect(cropShape, aspectRatio) {
  return cropShape === 'round' ? 1 : aspectRatio;
}

export function isCropAspectRatioLocked(cropShape) {
  return cropShape === 'round';
}
